import { useCallback, useState, useEffect } from 'react';
import { css } from '@emotion/react';
import {
  color,
  spacing,
  Icon,
  Typography,
  Button,
  Avatar,
  textStyleMedium,
  Tooltip,
  Popover,
  ActionList,
  Modal
} from '@uniquegood/realworld-studio-design';
import { useHistory, useParams } from 'react-router-dom';
import { onMobile } from '@/styles/responsive';
import { useModalState, useRequest, useUserRole, usePromotionModal } from '@/hooks';
import PlanBadge from '@/components/PlanBadge';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { coreApi } from '@/api';
import { ClickEventName, PromotionTriggerLocation, track } from '@/track';
import { channelsMapper } from '@/utils/channelsMapper';
import { BELT_BANNER_HEIGHT } from '@/constants/common';

const MENU_ITEMS = [
  { id: 'home', icon: 'home_solid', label: '채널 홈' },
  { id: 'feeds', icon: 'comment_dots_solid', label: '피드' },
  { id: 'community', icon: 'user_friends_solid', label: '커뮤니티' },
  { id: 'settings', icon: 'cog_solid', label: '설정' }
] as const;

const TOOLTIP_CONTENT: Record<Exclude<PlanType, 'Realworld'>, string> = {
  Free: '{요금제를 업그레이드}하면 더 많은 크레딧이 충전돼요!',
  Pro: '프로 혜택! 매달 500개 충전',
  Enterprise: '엔터프라이즈 혜택! 매달 10,000개 충전',
  Class: '클래스 혜택! 매달 10,000개 충전',
  Teacher: '티처 혜택! 매달 1,000개 충전'
};

/**
 * TODO: 스타일 개선
 */
// '숫자 + 개' 부분과 중괄호로 감싸진 부분에 색상을 적용하는 함수
const renderTooltipContent = (text: string) => {
  // 중괄호로 감싸진 부분과 숫자+개 부분을 모두 분리
  const parts = text.split(/(\{[^}]+\}|\d+(?:,\d+)*개)/);
  return (
    <>
      {parts.map((part, idx) => {
        const uniqueKey = `${text}-${part}-${idx}`;

        // 숫자 + 개 패턴 체크
        if (/^\d+(?:,\d+)*개$/.test(part)) {
          return (
            <Typography as="span" type="caption" key={uniqueKey} css={HighlightNumberStyle}>
              {part}
            </Typography>
          );
        }

        // 중괄호로 감싸진 부분 체크 (중괄호 제거하고 내용만 표시)
        if (/^\{[^}]+\}$/.test(part)) {
          const content = part.replace(/[{}]/g, '');
          return (
            <Typography
              as="span"
              type="caption"
              key={uniqueKey}
              css={css`
                background: linear-gradient(94deg, rgb(164, 78, 218) 0%, rgb(34, 152, 248) 100%);
                -webkit-background-clip: text;
                background-clip: text;
                color: transparent;
                font-weight: 700;
              `}
            >
              {content}
            </Typography>
          );
        }

        return (
          <Typography
            as="span"
            type="caption"
            css={css`
              font-weight: 700;
            `}
            key={uniqueKey}
          >
            {part}
          </Typography>
        );
      })}
    </>
  );
};

interface SidebarProps {
  selectedMenu: 'home' | 'feeds' | 'community' | 'settings';
}

function Sidebar({ selectedMenu }: SidebarProps) {
  const history = useHistory();
  const { appId } = useParams<AppParam>();

  const { data: channelData } = useRequest<RealWorldApp>(`/apps/${appId}`);
  const { data: creditValues } = useRequest<CommonResponseModel<RemainCreditInfoResponseModel>>(
    `/apps/${appId}/credit`
  );

  const { data: { data: applicationInfo } = {} } = useRequest<
    CommonResponseModel<ApplicationInfoResponseModel>
  >(`/apps/${appId}/info`);

  const { plan } = usePlanLimits();

  const { isEditor, isTester } = useUserRole();
  const [leavePopoverActive, setLeavePopoverActive] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const toggleLeavePopoverActive = useCallback(() => {
    setLeavePopoverActive((prev) => !prev);
  }, []);
  const { modal, openModal, closeModal } = useModalState();

  // 스크롤 상태 감지
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolling(window.scrollY > 0);
    };

    // 초기 상태 설정
    handleScroll();

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const { openPromotionModal } = usePromotionModal();

  const { data: me } = useRequest<MyInfo>('/api/me');
  const { data: { data: apps } = {} } = useRequest<CommonResponseModel<ApplicationResponseModel[]>>(
    '/apps',
    undefined,
    {
      headers: {
        'x-rwd-api-version': '1.1'
      }
    }
  );

  // 중앙에서 에러 토스트(예: utils/emitServerErrorToast 등)로 에러를 이미 잡아주지만,
  // 추가적인 UI 처리(예: 팝업 닫기, 추가 안내 등)나 후속 로직 제어를 위해 catch가 필요할 수 있습니다.
  // 예를 들어, 나가기 실패 시 팝업 상태를 재설정하거나, 사용자가 추가로 확인할 수 있도록 모달/토스트를 띄우는 용도입니다.
  const handleLeaveChannel = async () => {
    try {
      await coreApi.delete(`/apps/${appId}/leave`);

      // 구성원으로 포함된 다른 채널로 리다이렉트
      if (me && apps) {
        // 사용자가 구성원으로 포함된 채널 목록 가져오기
        const channels = channelsMapper(apps, me.id);
        // 현재 채널을 제외한 채널 목록 필터링
        const otherChannels = channels.filter((channel) => channel.id !== appId);

        if (otherChannels.length > 0) {
          // localStorage에서 기본 채널 ID 확인
          const defaultChannelId = localStorage.getItem('defaultChannelID');
          // 기본 채널이 있고 다른 채널 목록에 포함되어 있는지 확인
          const defaultChannel = defaultChannelId
            ? otherChannels.find((channel) => channel.id === defaultChannelId)
            : null;

          // 기본 채널이 있으면 기본 채널로, 없으면 첫 번째 채널로 이동
          const targetChannelId = defaultChannel?.id ?? otherChannels[0].id;
          history.push(`/apps/${targetChannelId}`);
        } else {
          // 구성원으로 포함된 채널이 없으면 채널 생성 페이지로 이동
          history.push('/createApp');
        }
      } else {
        // 사용자 정보나 채널 목록을 가져오지 못한 경우 채널 생성 페이지로 이동
        history.push('/createApp');
      }
    } catch (error) {
      // 에러는 중앙에서 토스트로 이미 처리함
      // 필요시 추가 안내 모달 등 UI 후처리 가능
      console.error('채널 나가기 실패', error);
    } finally {
      setLeavePopoverActive(false);
      closeModal();
    }
  };

  const openLeaveModal = () => {
    openModal({
      title: '이 채널을 나가시겠어요?',
      children: `채널을 나가면 내 채널 목록에서 ‘${channelData?.name}'가 삭제되며 소유자의 초대 없이는 다시 들어올 수 없어요.`,
      primaryAction: {
        icon: 'sign_out_alt_solid',
        content: '나가기',
        onAction: () => {
          handleLeaveChannel();
          const param = {
            channel_id: applicationInfo?.id,
            channel_name: applicationInfo?.name ?? '',
            use_purpose_type: applicationInfo?.usePurposeType ?? '',
            plan_type: applicationInfo?.planType ?? ''
          };
          track.onClick({
            clickEventName: ClickEventName.click_channel_leave_modal_leave,
            params: param
          });
        },
        type: 'destructive'
      },
      secondaryAction: {
        content: '취소',
        onAction: closeModal
      }
    });
  };

  const handleNavigate = (menu: 'home' | 'feeds' | 'community' | 'settings') => {
    if (menu === 'home') {
      history.push(`/apps/${appId}`);
    } else {
      history.push(`/apps/${appId}?tab=${menu}`);
    }
  };

  const totalCredit = (creditValues?.data?.freeCredit ?? 0) + (creditValues?.data?.paidCredit ?? 0);

  const remainingProjectsPercentage = applicationInfo?.projectCreationLimit
    ? (applicationInfo?.createdProjectCount / applicationInfo?.projectCreationLimit) * 100
    : 0;

  const handleUpgradePlanClick = () => {
    openPromotionModal({ triggerLocation: PromotionTriggerLocation.content_create_limit_sidebar });
  };

  const isContentLimitReached =
    applicationInfo?.projectCreationLimit && applicationInfo?.createdProjectCount
      ? applicationInfo.projectCreationLimit <= applicationInfo.createdProjectCount
      : false;

  return (
    <aside css={SidebarContainer(isContentLimitReached)}>
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          <linearGradient id="credit-icon-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: '#4a41ff', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#22f8e2', stopOpacity: 1 }} />
          </linearGradient>
        </defs>
      </svg>
      <div css={SidebarContent}>
        {isEditor && (
          <Popover
            activator={
              <Button
                type="basic"
                size="small"
                cssStyle={css`
                  position: absolute;
                  top: 16px;
                  left: 16px;
                `}
                onClick={toggleLeavePopoverActive}
              >
                <Icon icon="sign_out_alt_solid" size="20px" color={color.text_subdued} />
              </Button>
            }
            active={leavePopoverActive}
            onClose={() => {
              setLeavePopoverActive(false);
            }}
          >
            <ActionList
              items={[
                {
                  content: '채널 나가기',
                  destructive: true,
                  onAction: () => {
                    const param = {
                      channel_id: applicationInfo?.id,
                      channel_name: applicationInfo?.name ?? '',
                      use_purpose_type: applicationInfo?.usePurposeType ?? '',
                      plan_type: applicationInfo?.planType ?? ''
                    };
                    track.onClick({
                      clickEventName: ClickEventName.click_channel_leave,
                      params: param
                    });
                    openLeaveModal();
                  }
                }
              ]}
            />
          </Popover>
        )}
        {/* 채널 정보 영역 */}
        <div css={ChannelInfoSection}>
          <Avatar
            cssUserNameStyle={AvatarUserNameStyle}
            size="xLarge"
            source={channelData?.imageUrl}
          />
          <PlanBadge plan={plan} />
          <Typography as="h3" type="heading" cssStyle={ChannelNameStyle}>
            {channelData?.name || '채널 이름'}
          </Typography>

          <div css={ChannelMetaInfo}>
            <Typography as="span" type="caption" textColor="initial">
              공개한 콘텐츠 {applicationInfo?.releasedProjectCount ?? 0}개
            </Typography>
            <div css={Divider} />
            <Typography
              as="span"
              type="caption"
              cssStyle={css`
                color: ${color.interactive_primary_default};
              `}
            >
              팔로워 {applicationInfo?.followerCount ?? 0}명
            </Typography>
          </div>

          <div css={CreditInfo}>
            <div css={CreditRow}>
              <div css={GradientIconWrapper}>
                <Icon icon="special_solid" size="24px" />
              </div>
              <Typography as="span" type="body">
                {totalCredit} <span css={CreditLabel}>크레딧</span>
              </Typography>

              {plan !== 'Realworld' && (
                <Tooltip
                  preferredPosition="right"
                  content={
                    <Typography as="p" type="caption">
                      {renderTooltipContent(TOOLTIP_CONTENT[plan])}
                    </Typography>
                  }
                  dismissOnMouseOut
                >
                  <Icon
                    icon="question_circle_solid"
                    size="16px"
                    color={color.palette_gray_tint_04}
                  />
                </Tooltip>
              )}
            </div>
          </div>
        </div>

        {/* 메뉴 리스트 */}
        <div css={MenuSection}>
          {MENU_ITEMS.filter((item) => (isEditor || isTester ? item.id !== 'settings' : true)).map(
            (item) => (
              <button
                key={item.id}
                type="button"
                css={[MenuItem, selectedMenu === item.id && MenuItemActive]}
                onClick={() => {
                  handleNavigate(item.id);
                }}
              >
                <Icon
                  icon={item.icon}
                  size="24px"
                  color={color.text_subdued}
                  css={css`
                    margin: 0;
                  `}
                />
                <Typography as="span" type="body" textColor="initial">
                  {item.label}
                </Typography>
              </button>
            )
          )}
        </div>
      </div>

      {/* 하단 요금제 업그레이드 영역 */}
      {applicationInfo &&
        applicationInfo.projectCreationLimit &&
        (isContentLimitReached ? (
          <ContentLimitReachedWarning
            currentCount={applicationInfo?.createdProjectCount ?? 0}
            maxCount={applicationInfo?.projectCreationLimit ?? 0}
            isScrolling={isScrolling}
          />
        ) : (
          <div css={[UpgradeSectionBase]}>
            <div css={ContentLimitInfo}>
              <div css={LimitHeader}>
                <Typography as="p" type="heading" textColor="subdued" cssStyle={LimitTitle}>
                  만든 콘텐츠
                </Typography>
                <Typography as="p" type="button">
                  {applicationInfo?.createdProjectCount ?? 0}{' '}
                  <span css={LimitMax}>/ {applicationInfo?.projectCreationLimit ?? 0}</span>
                </Typography>
              </div>
              <div css={ProgressBar}>
                <div css={ProgressFill} style={{ width: `${remainingProjectsPercentage}%` }} />
              </div>
              <Button
                type="outlinePrimary"
                size="medium"
                onClick={handleUpgradePlanClick}
                cssStyle={UpgradeButton}
              >
                요금제 업그레이드
              </Button>
            </div>
          </div>
        ))}
      <Modal {...modal} />
    </aside>
  );
}

export default Sidebar;

const AvatarUserNameStyle = css`
  ${textStyleMedium};
  color: ${color.text_default};
  margin-bottom: ${spacing.margin.xsmall2};
`;

const SidebarContainer = (isContentLimitReached: boolean) => css`
  position: sticky;
  top: ${BELT_BANNER_HEIGHT}px;
  width: 233px;
  min-width: 233px;
  height: calc(100vh - ${BELT_BANNER_HEIGHT + (isContentLimitReached ? 0 : 32)}px);
  min-height: 684px;
  background-color: ${color.surface_default_default};
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  justify-content: space-between;

  ${onMobile} {
    display: none;
  }
`;

const SidebarContent = css`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${spacing.common.medium};
  padding: 48px ${spacing.common.medium} ${spacing.common.large};
  flex: 1;
  min-height: 0;
`;

const ChannelInfoSection = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing.common.small};
  padding: ${spacing.common.medium} 0;
`;

const ChannelNameStyle = css`
  text-align: center;
  word-break: break-word;
  max-width: 100%;
`;

const ChannelMetaInfo = css`
  display: flex;
  align-items: center;
  gap: ${spacing.common.small};
  height: 16px;
`;

const Divider = css`
  width: 1px;
  height: 16px;
  background-color: ${color.border_default_subdued};
  transform: rotate(180deg);
`;

const CreditInfo = css`
  width: 100%;
  display: flex;
  gap: ${spacing.common.small};
  align-items: center;
  justify-content: center;
`;

const CreditRow = css`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const GradientIconWrapper = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  svg {
    fill: url(#credit-icon-gradient) !important;
  }

  path {
    fill: url(#credit-icon-gradient) !important;
  }
`;

const CreditLabel = css`
  color: ${color.text_disabled};
`;

const MenuSection = css`
  display: flex;
  flex-direction: column;
  gap: 0;
  height: 100%;
`;

const MenuItem = css`
  display: flex;
  align-items: center;
  gap: ${spacing.common.small};
  padding: ${spacing.common.small} 12px;
  border-radius: ${spacing.borderRadius.medium};
  margin-bottom: ${spacing.common.medium};
  background-color: transparent;
  border: none;
  cursor: pointer;
  color: ${color.text_default};
  transition: background-color 0.2s;

  &:hover {
    background-color: ${color.surface_default_subdued};
  }
`;

const MenuItemActive = css`
  background-color: ${color.surface_default_subdued};
`;

const UpgradeSectionBase = css`
  padding: ${spacing.common.medium};
  display: flex;
  flex-direction: column;
  gap: ${spacing.common.large};
  background-color: ${color.surface_default_default};
  width: 233px;
  box-sizing: border-box;
  flex-shrink: 0;
  z-index: 1;
`;

const ContentLimitInfo = css`
  background-color: ${color.surface_default_subdued};
  border: 1px solid ${color.border_default_subdued};
  border-radius: ${spacing.borderRadius.medium};
  padding: ${spacing.common.medium};
  display: flex;
  flex-direction: column;
`;

const LimitHeader = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
`;

const LimitTitle = css`
  margin: 0;
`;

const LimitMax = css`
  color: ${color.text_disabled};
`;

const ProgressBar = css`
  width: 100%;
  height: 8px;
  background-color: ${color.border_default_subdued};
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: ${spacing.common.medium};
`;

const ProgressFill = css`
  height: 100%;
  background-color: ${color.interactive_primary_default};
  transition: width 0.3s ease;
`;

const UpgradeButton = css`
  width: 100%;
`;

interface ContentLimitReachedProps {
  currentCount: number;
  maxCount: number;
  isScrolling: boolean;
}

function ContentLimitReachedWarning({
  currentCount,
  maxCount,
  isScrolling
}: ContentLimitReachedProps) {
  const percentage = (currentCount / maxCount) * 100;

  return (
    <div css={[WarningSectionBase]} style={{ marginBottom: isScrolling ? 0 : 32 }}>
      <div css={WarningBox}>
        <div css={WarningHeader}>
          <Typography as="p" type="heading" cssStyle={WarningTitle}>
            콘텐츠 생성 제한
          </Typography>
          <Typography as="p" type="button">
            {currentCount} <span css={WarningMax}>/ {maxCount}</span>
          </Typography>
        </div>
        <div css={WarningProgressBar}>
          <div css={WarningProgressFill} style={{ width: `${percentage}%` }} />
        </div>
      </div>
    </div>
  );
}

const WarningSectionBase = css`
  padding: ${spacing.common.medium};
  display: flex;
  flex-direction: column;
  gap: ${spacing.common.large};
  background-color: ${color.surface_default_default};
  width: 233px;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const WarningBox = css`
  background-color: #feedec;
  border: 1px solid #d53d40;
  border-radius: ${spacing.borderRadius.medium};
  padding: ${spacing.common.medium};
  display: flex;
  flex-direction: column;
  gap: ${spacing.common.medium};
`;

const WarningHeader = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const WarningTitle = css`
  margin: 0;
  color: #d53d40;
`;

const WarningMax = css`
  color: ${color.text_disabled};
`;

const WarningProgressBar = css`
  width: 100%;
  height: 8px;
  background-color: ${color.surface_default_subdued};
  border-radius: 2px;
  overflow: hidden;
`;

const WarningProgressFill = css`
  height: 100%;
  background-color: #d53d40;
  border-radius: 2px;
  transition: width 0.3s ease;
`;

const HighlightNumberStyle = css`
  background: linear-gradient(94deg, rgb(65, 154, 255) 0%, rgb(34, 184, 248) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  font-weight: 700;
`;
